import React, { useMemo, useState } from "react";
import CompanyCard from "../components/CompanyCard";
import IdentityChip from "../components/IdentityChip";
import { Company } from "../schemas/auth";
import { companyKeyFor } from "../hooks/useAuthFlow";

interface IdentityChipProps {
  label: string;
  onLogout: () => void;
  onSwitchUser: () => void;
}

interface CompanyChooserProps {
  companies: Company[];
  onSelect: (company: Company) => void;
  onBack: () => void;
  loading: boolean;
  error: string | null;
  // Opt-in client-side filter for large company lists (e.g. support users with
  // 300+ chains). Default off so the standard portal/company chooser is
  // unchanged. ZC-5658.
  filterable?: boolean;
  /**
   * ZC-5707: When present, renders an IdentityChip in the header showing who
   * the user is signed in as (resume session label). Omit for non-resume logins.
   */
  identity?: IdentityChipProps;
}

export default function CompanyChooser({
  companies,
  onSelect,
  onBack,
  loading,
  error,
  filterable = false,
  identity,
}: CompanyChooserProps) {
  const [query, setQuery] = useState("");

  const shown = useMemo(() => {
    if (!filterable) return companies;
    const q = query.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter(
      (company) =>
        (company.company_name?.toLowerCase().includes(q) ?? false) ||
        String(company.chain_code).includes(q)
    );
  }, [companies, filterable, query]);

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <button
          type="button"
          onClick={onBack}
          className="btn btn-link p-0 text-muted d-flex align-items-center gap-1"
          aria-label="Go back"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </button>
        {identity && (
          <IdentityChip
            label={identity.label}
            onLogout={identity.onLogout}
            onSwitchUser={identity.onSwitchUser}
          />
        )}
      </div>

      <h1 className="h3 fw-bold text-dark mb-1">Select your company</h1>
      <p className="text-muted mb-4">
        Your account is linked to multiple companies. Choose which one to sign
        in to.
      </p>

      {error && (
        <div className="alert alert-danger py-2 mb-3" role="alert">
          {error}
        </div>
      )}

      {filterable && (
        <>
          <input
            type="text"
            className="form-control mb-2"
            placeholder="Filter by name or code"
            aria-label="Filter companies"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <p className="text-muted small mb-3">
            {shown.length} of {companies.length}
          </p>
        </>
      )}

      {filterable && shown.length === 0 ? (
        <p className="text-muted">No companies match your search.</p>
      ) : (
        <div
          role="list"
          aria-label="Available companies"
          // When filterable (support users with many chains), the list becomes
          // its own scroll region so the filter + count above stay pinned while
          // only the cards scroll. Capped to 60vh so the centred auth card never
          // grows past the viewport. Non-filterable callers keep today's
          // unbounded layout unchanged. ZC-5658.
          className={filterable ? "overflow-auto" : undefined}
          style={filterable ? { maxHeight: "60vh" } : undefined}
        >
          {shown.map((company) => (
            <div key={companyKeyFor(company)} role="listitem">
              <CompanyCard
                company={company}
                onSelect={onSelect}
                disabled={loading}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
